import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericGet } from '@/_shared/types';
import { GenericApiError } from '@/_shared/types/apiError';
import { getCurrentOrganization } from '@/core/state/selectors';
import { API_ORGANIZATIONS_ORGANIZATIONID_DEPARTMENTS_DEPARTMENTID_TEAMS_TEAMID_MEMBERS_PATH_KEY } from '@/swagger/constants/anagraphics-keys';
import { API_GET_DEPARTMENT_TEAM_MEMBERS, KEY_GET_DEPARTMENT_TEAM_MEMBERS } from '../constants';
import { MOCK_RESPONSE_GET_DEPARTMENT_TEAM_MEMBERS } from '../mocks';
import { ResponseGetDepartmentTeamMembers } from '../types';

type FullRequestType = ReturnType<typeof createGeneralCall>['selectors']['request'] & {
  payload: Omit<CallParamsGetDepartmentTeamMembers, 'successSaga' | 'errorSaga'>;
};

type RequestPathGetDepartmentTeamMembers = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_DEPARTMENTS_DEPARTMENTID_TEAMS_TEAMID_MEMBERS_PATH_KEY
>['request']['path'];

type RequestQueryGetDepartmentTeamMembers = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_DEPARTMENTS_DEPARTMENTID_TEAMS_TEAMID_MEMBERS_PATH_KEY
>['request']['query'];

type CallParamsGetDepartmentTeamMembers = RequestQueryGetDepartmentTeamMembers &
  Omit<RequestPathGetDepartmentTeamMembers, 'organizationId'> & {
    successSaga?: CallbackSaga;
    errorSaga?: CallbackSaga;
  };

export type CallResponseGetDepartmentTeamMembers = ResponseGetDepartmentTeamMembers;

type CallErrorGetDepartmentTeamMembers = GenericApiError;

type CallSelectorsGetDepartmentTeamMembers = {
  data: ResponseGetDepartmentTeamMembers['data'];
  totalItems: ResponseGetDepartmentTeamMembers['totalItems'];
  fullRequest: FullRequestType;
};

let internalPayload = {};

export const getDepartmentTeamMembersCall = createGeneralCall<
  CallParamsGetDepartmentTeamMembers,
  CallResponseGetDepartmentTeamMembers,
  CallErrorGetDepartmentTeamMembers,
  CallSelectorsGetDepartmentTeamMembers
>({
  id: KEY_GET_DEPARTMENT_TEAM_MEMBERS,
  method: 'get',
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_GET_DEPARTMENT_TEAM_MEMBERS(
        org,
        payload.departmentId,
        payload.teamId,
        payload.excludeMemberType,
      );
    },
  paramsSelector:
    ({ payload }) =>
    () => {
      internalPayload = { ...payload };
      return payload;
    },
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_DEPARTMENT_TEAM_MEMBERS : undefined,
  selectors: {
    data: ({ selectors }) => createSelector(selectors.response, res => res?.data || []),
    totalItems: ({ selectors }) => createSelector(selectors.response, res => res?.totalItems || 0),
    fullRequest: ({ selectors }) =>
      createSelector(selectors.request, request => {
        return {
          ...request,
          payload: { ...internalPayload },
        } as unknown as FullRequestType;
      }),
  },
});
